// ═══════════════════════════════════════════════════════════════════════
// triggerSystem.jsx — Direct + Indirect triggers with full attribute lens
// ═══════════════════════════════════════════════════════════════════════
// Replaces the old TriggerDetails. New surface area:
//
//   getDirectTriggers(iface)         — own triggers, with sane fallback
//   getIndirectTriggers(iface)       — triggers cascading from iface.deps
//   getRemainingAttributes(iface, t) — attrMap minus trigger.recommendedMapping
//   groupAttrsByCategory(attrs)
//   <TriggerExplorer iface={...} state={state} ...> — drop-in replacement
//                                       for the old `iface.triggers.map(...)`
//                                       block in TaskDrawer.
//
// The new expanded-trigger view shows three collapsible sub-sections:
//   1. ✅ Recommended (mandatory) mappings — trigger.recommendedMapping
//   2. ✨ Optional (remaining) attributes  — iface.attrMap \ recommendedMapping,
//                                            grouped by attribute category
//   3. ⚙️  Custom attributes               — user-added per trigger
// Plus the existing technical/preconditions/SLA/etc. detail blocks.
//
// EXTERNAL DEPENDENCIES
//   - IfacesData: IFACES, TRIGGER_TYPES
//   - The host passes in its existing CommentSection/CustomTriggerAttributes
//     components as props so this module stays plug-and-play.
//
// ═══════════════════════════════════════════════════════════════════════

import React, { useMemo, useState } from "react";
import { IFACES, TRIGGER_TYPES } from "./IfacesData";
import { T, FONTS } from "./theme";

// ═══════════════════════════════════════════════════════════════════════
// PURE HELPERS
// ═══════════════════════════════════════════════════════════════════════

/**
 * getDirectTriggers — interface's own triggers; if none, synthesize a
 * single sensible default so every interface has at least one card to expand.
 * Synthesized triggers are flagged with `synthesized: true` so the UI can
 * render a "(auto-derived)" hint.
 */
export function getDirectTriggers(iface) {
  if (!iface) return [];
  if (iface.triggers && iface.triggers.length > 0) {
    return iface.triggers.map(t => ({ ...t, _origin: "direct" }));
  }
  const objectName = (iface.bo || "").split("→")[0]?.trim() || "object";
  const defaultType = (iface.freq || "").toLowerCase().includes("event") ? "STATE_CHANGE" : "UPDATE";
  return [{
    id: `${iface.id}-DEFAULT`,
    name: `${objectName} sync — ${iface.from?.split(" ")[0] || "source"} → ${iface.to?.split(" ")[0] || "target"}`,
    type: defaultType,
    source: iface.from,
    sourceEvent: `${objectName} state change`,
    description:
      `Auto-derived trigger: no explicit trigger defined for this interface in IfacesData. ` +
      `Best-effort default based on ${iface.freq || "unknown frequency"} integration. ` +
      (iface.about ? `Context: ${iface.about.substring(0, 200)}…` : ""),
    latency: "TBD",
    synthesized: true,
    _origin: "direct",
    recommendedMapping: [],   // empty so the optional-from-attrMap section
                              // surfaces the full attrMap as remaining
    optionalMapping: [],
  }];
}

/**
 * getIndirectTriggers — for each iface in iface.deps, return its direct
 * triggers tagged with the source interface so the UI can label them as
 * cascaded.  Walks ONE level (no recursion) to keep noise manageable; if you
 * want transitive, set walkDepth > 1.
 */
export function getIndirectTriggers(iface, walkDepth = 1) {
  if (!iface || !iface.deps?.length) return [];
  const out = [];
  const seen = new Set([iface.id]);

  function walk(currentId, depthLeft) {
    if (depthLeft <= 0) return;
    const cur = IFACES.find(f => f.id === currentId);
    if (!cur || !cur.deps?.length) return;
    for (const depId of cur.deps) {
      if (seen.has(depId)) continue;
      seen.add(depId);
      const depIface = IFACES.find(f => f.id === depId);
      if (!depIface) continue;
      const depTriggers = getDirectTriggers(depIface);
      for (const t of depTriggers) {
        out.push({
          ...t,
          _origin: "indirect",
          sourceInterfaceId: depIface.id,
          sourceInterfaceName: depIface.name,
        });
      }
      walk(depId, depthLeft - 1);
    }
  }
  walk(iface.id, walkDepth);
  return out;
}

/**
 * getRemainingAttributes — iface.attrMap \ trigger.recommendedMapping
 * (matched by destination field, since the same source can map to
 * multiple destinations and vice versa, and dst is the canonical target).
 */
export function getRemainingAttributes(iface, trigger) {
  if (!iface?.attrMap) return [];
  const usedDst = new Set((trigger?.recommendedMapping || []).map(m => m.dst));
  return iface.attrMap.filter(a => a && a.dst && !usedDst.has(a.dst));
}

/** Group attribute-mapping rows by their `category` field (defaults: "additional") */
export function groupAttrsByCategory(attrs) {
  const groups = {};
  for (const a of attrs || []) {
    const cat = a.category || "additional";
    (groups[cat] = groups[cat] || []).push(a);
  }
  return groups;
}

// Order categories by importance / typical interest
const CATEGORY_ORDER = ["core", "quality", "audit", "security", "metadata", "additional"];
const CATEGORY_COLOR = {
  core:     T.green,
  quality:  T.amber,
  audit:    T.purple,
  security: T.red,
  metadata: T.steel,
  additional: T.cyan,
};

function sortedCategories(groups) {
  const keys = Object.keys(groups);
  return keys.sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

// ═══════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════
const S = {
  sectionTitle: (color) => ({
    fontFamily: FONTS.mono,
    fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
    color, fontWeight: 600, marginBottom: 8,
    display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
    userSelect: "none",
  }),
  chevron: (open) => ({
    display: "inline-block", width: 10,
    transform: open ? "rotate(90deg)" : "none",
    transition: "transform .15s ease",
  }),
  pill: (color) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "2px 8px", borderRadius: 12,
    fontFamily: FONTS.mono, fontSize: 8,
    border: `1px solid ${color}40`,
    background: `${color}12`, color,
  }),
  card: (color, indirect) => ({
    marginBottom: 10,
    border: `1px ${indirect ? "dashed" : "solid"} ${color}40`,
    borderLeft: `3px solid ${color}`,
    borderRadius: 8, overflow: "hidden",
    background: T.bg2,
  }),
  cardHeader: (color, expanded) => ({
    padding: "10px 14px",
    background: `${color}08`,
    display: "flex", alignItems: "center", gap: 12,
    cursor: "pointer",
    borderBottom: expanded ? `1px solid ${color}30` : "none",
  }),
  cardBody: { padding: "14px 18px", background: T.bg2 },
  detailSection: {
    marginBottom: 14,
    paddingBottom: 12,
    borderBottom: `1px solid ${T.border2}`,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 9 },
  th: {
    textAlign: "left", padding: "6px 8px",
    color: T.text2, borderBottom: `1px solid ${T.border}`,
    fontFamily: FONTS.mono, fontSize: 8,
    textTransform: "uppercase", letterSpacing: 1,
  },
  td: { padding: "6px 8px", borderBottom: `1px solid ${T.border2}` },
  badge: (color) => ({
    display: "inline-block", padding: "1px 6px",
    borderRadius: 8, fontSize: 7,
    background: `${color}20`, color,
    fontFamily: FONTS.mono,
    textTransform: "uppercase", letterSpacing: 0.5,
  }),
  catHeader: (color) => ({
    display: "flex", alignItems: "center", gap: 8,
    padding: "8px 0 4px",
    color, fontSize: 9, fontWeight: 600,
    fontFamily: FONTS.mono,
    textTransform: "uppercase", letterSpacing: 1,
  }),
  emptyHint: {
    padding: "10px 12px", fontSize: 9, color: T.text3,
    fontStyle: "italic", textAlign: "center",
    background: T.bg3, borderRadius: 4,
  },
  groupHeader: (open, color, count) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 14px",
    background: open ? `${color}10` : `${color}05`,
    borderBottom: open ? `1px solid ${color}30` : `1px solid ${T.border2}`,
    borderTop: `2px solid ${color}`,
    cursor: "pointer", userSelect: "none",
    marginTop: 12,
  }),
  groupHeaderText: (color) => ({
    fontFamily: FONTS.header,
    fontSize: 11, fontWeight: 700, color,
    letterSpacing: 1,
    display: "flex", alignItems: "center", gap: 8,
  }),
};

// ═══════════════════════════════════════════════════════════════════════
// TriggerExplorer — container that lists DIRECT + INDIRECT triggers
// ═══════════════════════════════════════════════════════════════════════
//
// Props:
//   iface                       — full interface object
//   state                       — app state (for state.comments + customTriggerAttrs)
//   dispatch                    — reducer dispatch
//   CommentSection?             — host's CommentSection component
//   CustomTriggerAttributes?    — host's CustomTriggerAttributes component
//
//   The two component props are optional. If omitted, those features render
//   as a stub note. This keeps the module independent.
//
// ═══════════════════════════════════════════════════════════════════════
export function TriggerExplorer({
  iface, state, dispatch,
  CommentSection,
  CustomTriggerAttributes,
}) {
  const direct = useMemo(() => getDirectTriggers(iface), [iface]);
  const indirect = useMemo(() => getIndirectTriggers(iface), [iface]);

  const [showDirect, setShowDirect] = useState(true);
  const [showIndirect, setShowIndirect] = useState(false);
  const [expandedTriggers, setExpandedTriggers] = useState(new Set());

  const toggleTrigger = (id) => {
    setExpandedTriggers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const cardProps = {
    iface, state, dispatch,
    expandedTriggers, toggleTrigger,
    CommentSection, CustomTriggerAttributes,
  };

  return (
    <div>
      {/* Direct triggers */}
      <div
        style={S.groupHeader(showDirect, T.pink, direct.length)}
        onClick={() => setShowDirect(s => !s)}
      >
        <span style={S.groupHeaderText(T.pink)}>
          <span style={S.chevron(showDirect)}>▸</span>
          ⚡ DIRECT TRIGGERS ({direct.length})
        </span>
        <span style={S.badge(T.pink)}>fires from {iface.from?.split(" ")[0] || iface.id}</span>
      </div>
      {showDirect && direct.map(t => (
        <TriggerCard key={t.id} trigger={t} {...cardProps} />
      ))}

      {/* Indirect / cascaded triggers */}
      <div
        style={S.groupHeader(showIndirect, T.purple, indirect.length)}
        onClick={() => setShowIndirect(s => !s)}
      >
        <span style={S.groupHeaderText(T.purple)}>
          <span style={S.chevron(showIndirect)}>▸</span>
          🔗 INDIRECT TRIGGERS ({indirect.length})
        </span>
        <span style={S.badge(T.purple)}>cascaded from upstream interfaces</span>
      </div>
      {showIndirect && indirect.length === 0 && (
        <div style={S.emptyHint}>
          No upstream dependencies declared on this interface.
          Indirect triggers come from interfaces listed in <code>iface.deps</code>.
        </div>
      )}
      {showIndirect && indirect.map(t => (
        <TriggerCard
          key={`${t.sourceInterfaceId}::${t.id}`}
          trigger={t}
          {...cardProps}
        />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TriggerCard — single trigger row, expandable
// ═══════════════════════════════════════════════════════════════════════
function TriggerCard({
  trigger, iface, state, dispatch,
  expandedTriggers, toggleTrigger,
  CommentSection, CustomTriggerAttributes,
}) {
  const meta = TRIGGER_TYPES[trigger.type] || { label: trigger.type, color: T.steel, icon: "⚡" };
  const cardId = trigger._origin === "indirect"
    ? `${trigger.sourceInterfaceId}::${trigger.id}`
    : trigger.id;
  const expanded = expandedTriggers.has(cardId);
  const isIndirect = trigger._origin === "indirect";
  const color = isIndirect ? T.purple : meta.color;

  // Sub-section collapse state — kept local so each card has its own
  const [openRecommended, setOpenRecommended] = useState(true);
  const [openOptional, setOpenOptional]       = useState(true);
  const [openCustom, setOpenCustom]           = useState(false);
  const [openTechnical, setOpenTechnical]     = useState(false);
  const [openPrecond, setOpenPrecond]         = useState(false);
  const [openConfig, setOpenConfig]           = useState(false);
  const [openComments, setOpenComments]       = useState(false);

  const recommended = trigger.recommendedMapping || [];
  const remaining = useMemo(
    () => getRemainingAttributes(iface, trigger),
    [iface, trigger]
  );
  const remainingByCat = useMemo(
    () => groupAttrsByCategory(remaining),
    [remaining]
  );
  const remainingTotal = remaining.length;

  const triggerCommentKey = `${iface.id}-TRG-${cardId}`;
  const commentCount = state?.comments?.[triggerCommentKey]?.length || 0;
  const customAttrCount = state?.customTriggerAttrs?.[cardId]?.length || 0;

  return (
    <div style={S.card(color, isIndirect)}>
      {/* Card header */}
      <div style={S.cardHeader(color, expanded)} onClick={() => toggleTrigger(cardId)}>
        <span style={{ fontSize: 20 }}>{meta.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color }}>
              {trigger.name}
            </span>
            <span style={S.pill(meta.color)}>{meta.label}</span>
            {isIndirect && (
              <span style={S.pill(T.purple)}>
                ↩ via {trigger.sourceInterfaceId}
              </span>
            )}
            {trigger.synthesized && (
              <span style={S.pill(T.amber)}>auto-derived</span>
            )}
            {trigger.latency && (
              <span style={S.pill(T.cyan)}>⏱ {trigger.latency}</span>
            )}
            {commentCount > 0 && <span style={S.pill(T.gold)}>💬 {commentCount}</span>}
            {customAttrCount > 0 && <span style={S.pill(T.purple)}>⚙ {customAttrCount}</span>}
          </div>
          <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5 }}>
            {trigger.description}
          </div>
          {(trigger.sourceEvent || trigger.condition) && (
            <div style={{ fontSize: 8.5, color, marginTop: 4, fontFamily: FONTS.mono }}>
              {trigger.sourceEvent}
              {trigger.condition && ` · cond: ${trigger.condition}`}
            </div>
          )}
        </div>
        <span style={{ ...S.chevron(expanded), fontSize: 14, color, alignSelf: "center" }}>▸</span>
      </div>

      {expanded && (
        <div style={S.cardBody}>

          {/* ─── Section 1: Recommended (mandatory) mappings ─── */}
          <div style={S.detailSection}>
            <div
              style={S.sectionTitle(T.green)}
              onClick={() => setOpenRecommended(o => !o)}
            >
              <span style={S.chevron(openRecommended)}>▸</span>
              ✅ Recommended (Mandatory) Mappings
              <span style={S.badge(T.green)}>{recommended.length}</span>
            </div>
            {openRecommended && (
              recommended.length > 0 ? (
                <MappingTable rows={recommended} colorScheme="recommended" />
              ) : (
                <div style={S.emptyHint}>
                  No mandatory mappings defined for this trigger.
                  {trigger.synthesized && " (Auto-derived trigger — see attribute catalogue below.)"}
                </div>
              )
            )}
          </div>

          {/* ─── Section 2: Optional remaining attributes (from iface.attrMap) ─── */}
          <div style={S.detailSection}>
            <div
              style={S.sectionTitle(T.purple)}
              onClick={() => setOpenOptional(o => !o)}
            >
              <span style={S.chevron(openOptional)}>▸</span>
              ✨ Optional Remaining Attributes
              <span style={S.badge(T.purple)}>{remainingTotal}</span>
              <span style={{ ...S.badge(T.text3), marginLeft: 4 }}>
                of {iface.attrMap?.length || 0} in catalogue
              </span>
            </div>
            {openOptional && (
              remainingTotal > 0 ? (
                <RemainingAttrsView remainingByCat={remainingByCat} />
              ) : (
                <div style={S.emptyHint}>
                  Every catalogued attribute is already covered by recommended mappings.
                  {iface.attrMap == null && " (No attrMap defined on this interface.)"}
                </div>
              )
            )}
          </div>

          {/* ─── Section 3: Custom attributes ─── */}
          <div style={S.detailSection}>
            <div
              style={S.sectionTitle(T.purple)}
              onClick={() => setOpenCustom(o => !o)}
            >
              <span style={S.chevron(openCustom)}>▸</span>
              ⚙️ Custom Attributes
              <span style={S.badge(T.purple)}>{customAttrCount}</span>
            </div>
            {openCustom && (
              CustomTriggerAttributes ? (
                <CustomTriggerAttributes
                  triggerId={cardId}
                  customAttrs={state?.customTriggerAttrs}
                  onAddAttribute={(triggerId, attr) =>
                    dispatch({ type: "ADD_TRIGGER_ATTRIBUTE", triggerId, attr })}
                  onDeleteAttribute={(triggerId, attrIndex) =>
                    dispatch({ type: "DELETE_TRIGGER_ATTRIBUTE", triggerId, attrIndex })}
                />
              ) : (
                <div style={S.emptyHint}>
                  Custom attribute editor not wired up — pass <code>CustomTriggerAttributes</code> prop.
                </div>
              )
            )}
          </div>

          {/* ─── Technical specs ─── */}
          {trigger.technicalDetails && (
            <CollapsibleDetails
              title="🔧 Technical Specifications"
              color={T.cyan}
              open={openTechnical}
              onToggle={() => setOpenTechnical(o => !o)}
              data={trigger.technicalDetails}
            />
          )}

          {trigger.sla && (
            <CollapsibleDetails
              title="⏱️ SLA Requirements" color={T.green}
              open={openTechnical} onToggle={() => setOpenTechnical(o => !o)}
              data={trigger.sla} forceMounted={openTechnical}
              renderInline
            />
          )}

          {/* ─── Pre-conditions ─── */}
          {trigger.preConditions?.length > 0 && (
            <div style={S.detailSection}>
              <div
                style={S.sectionTitle(T.gold)}
                onClick={() => setOpenPrecond(o => !o)}
              >
                <span style={S.chevron(openPrecond)}>▸</span>
                📋 Pre-conditions
                <span style={S.badge(T.gold)}>{trigger.preConditions.length}</span>
              </div>
              {openPrecond && (
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 9.5, color: T.text2, lineHeight: 1.6 }}>
                  {trigger.preConditions.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* ─── Configuration steps ─── */}
          {trigger.configSteps?.length > 0 && (
            <div style={S.detailSection}>
              <div
                style={S.sectionTitle(T.amber)}
                onClick={() => setOpenConfig(o => !o)}
              >
                <span style={S.chevron(openConfig)}>▸</span>
                ⚙️ Configuration Steps
                <span style={S.badge(T.amber)}>{trigger.configSteps.length}</span>
              </div>
              {openConfig && (
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 9.5, color: T.text2, lineHeight: 1.6 }}>
                  {trigger.configSteps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
            </div>
          )}

          {/* ─── Error handling / monitoring / security / rollback ─── */}
          {trigger.errorHandling && (
            <DetailKV title="❌ Error Handling" color={T.red} data={trigger.errorHandling} />
          )}
          {trigger.monitoring && (
            <DetailKV title="📊 Monitoring" color={T.blue} data={trigger.monitoring} />
          )}
          {trigger.security && (
            <DetailKV title="🔒 Security" color={T.cyan} data={trigger.security} />
          )}
          {trigger.rollback && (
            <div style={S.detailSection}>
              <div style={S.sectionTitle(T.amber)}>🔄 Rollback Procedure</div>
              {trigger.rollback.steps && (
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 9.5, color: T.text2, lineHeight: 1.5 }}>
                  {trigger.rollback.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
              <div style={{ fontSize: 8.5, color: T.text3, marginTop: 6 }}>
                <strong>Estimated time:</strong> {trigger.rollback.estimatedTime || "—"}
                {" · "}
                <strong>Data loss:</strong> {trigger.rollback.dataLoss || "—"}
              </div>
            </div>
          )}

          {/* ─── Comments ─── */}
          <div style={{ ...S.detailSection, borderBottom: "none" }}>
            <div
              style={S.sectionTitle(T.amber)}
              onClick={() => setOpenComments(o => !o)}
            >
              <span style={S.chevron(openComments)}>▸</span>
              💬 Trigger Comments
              {commentCount > 0 && <span style={S.badge(T.amber)}>{commentCount}</span>}
            </div>
            {openComments && (
              CommentSection ? (
                <CommentSection
                  targetId={triggerCommentKey}
                  targetType="trigger"
                  comments={state?.comments}
                  onSaveComment={(targetId, comment) =>
                    dispatch({ type: "ADD_COMMENT", targetId, comment })}
                  onDeleteComment={(targetId, commentIndex) =>
                    dispatch({ type: "DELETE_COMMENT", targetId, commentIndex })}
                />
              ) : (
                <div style={S.emptyHint}>
                  Comment UI not wired up — pass <code>CommentSection</code> prop.
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MappingTable: a small table for src→dst attribute mapping rows ─────
function MappingTable({ rows, colorScheme }) {
  const isOptional = colorScheme === "optional";
  const fadeOpacity = isOptional ? 0.85 : 1;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Source</th>
            <th style={{ ...S.th, width: 18 }}></th>
            <th style={S.th}>Destination</th>
            <th style={S.th}>Transform</th>
            <th style={S.th}>Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ opacity: fadeOpacity }}>
              <td style={{ ...S.td, fontFamily: "monospace", color: T.cyan }}>{r.src}</td>
              <td style={S.td}>→</td>
              <td style={{ ...S.td, fontFamily: "monospace", color: T.green }}>{r.dst}</td>
              <td style={{ ...S.td, color: T.text2, fontSize: 8.5 }}>{r.xform || "—"}</td>
              <td style={{ ...S.td, color: T.text2, fontSize: 8.5 }}>{r.note || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── RemainingAttrsView: attributes grouped by category ─────────────────
function RemainingAttrsView({ remainingByCat }) {
  const cats = sortedCategories(remainingByCat);
  return (
    <div>
      {cats.map(cat => {
        const color = CATEGORY_COLOR[cat] || T.cyan;
        const rows = remainingByCat[cat];
        return (
          <div key={cat} style={{ marginBottom: 10 }}>
            <div style={S.catHeader(color)}>
              <span>{cat}</span>
              <span style={S.badge(color)}>{rows.length}</span>
            </div>
            <MappingTable rows={rows} colorScheme="optional" />
          </div>
        );
      })}
    </div>
  );
}

// ─── CollapsibleDetails: render an object's k/v as a small grid ──────────
function CollapsibleDetails({ title, color, open, onToggle, data, renderInline = false, forceMounted = false }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return null;
  return (
    <div style={S.detailSection}>
      <div style={S.sectionTitle(color)} onClick={onToggle}>
        <span style={S.chevron(open)}>▸</span>
        {title}
        <span style={S.badge(color)}>{entries.length}</span>
      </div>
      {(open || forceMounted) && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 8,
          fontSize: 9,
        }}>
          {entries.map(([k, v]) => (
            <div key={k} style={{ background: T.bg3, padding: "6px 8px", borderRadius: 4 }}>
              <div style={{ fontSize: 8, color: T.text3, textTransform: "uppercase", letterSpacing: 1 }}>
                {k}
              </div>
              <div style={{ color: T.text, fontFamily: "monospace", fontSize: 9, marginTop: 2, wordBreak: "break-word" }}>
                {renderValue(v)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Small uncollapsible variant used for the simpler detail blocks
function DetailKV({ title, color, data }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return null;
  return (
    <div style={S.detailSection}>
      <div style={S.sectionTitle(color)}>{title}</div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: 8,
      }}>
        {entries.map(([k, v]) => (
          <div key={k} style={{ background: T.bg3, padding: "6px 8px", borderRadius: 4 }}>
            <div style={{ fontSize: 8, color: T.text3, textTransform: "uppercase", letterSpacing: 1 }}>
              {k}
            </div>
            <div style={{ color: T.text, fontFamily: "monospace", fontSize: 9, marginTop: 2, wordBreak: "break-word" }}>
              {renderValue(v)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderValue(v) {
  if (v == null) return <span style={{ color: T.text3 }}>—</span>;
  if (typeof v === "boolean") return v ? "✓ enabled" : "✗ disabled";
  if (typeof v === "object") {
    return (
      <pre style={{ margin: 0, fontSize: 8.5, color: T.text2, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(v, null, 1)}
      </pre>
    );
  }
  return String(v);
}

export default TriggerExplorer;
